import { useEffect } from 'react';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { languageOptions, passwordRegEx } from '@app/constants';
import { paths } from '@app/constants/paths.constant';
import { changeLanguage, getCurrentAppTheme, useDetectLanguage, useHydration } from '@app/utils';
import { Language } from '@data';
import { useSetPassword } from '@data/hooks';
import { useAppUserStore } from '@data/storage';
import {
  Button,
  Card,
  AntdConfigProvider,
  AntdForm,
  AntdGrid,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  antdNotification,
  useTheme,
  isYWC,
  YWCLogoSmall,
} from '@ui';
import { InputPassword } from '../components';

const { useBreakpoint } = AntdGrid;
const { Text, Title } = AntdTypography;

const SetupPasswordPage = () => {
  const [form] = AntdForm.useForm();
  const navigate = useNavigate();
  const { sm, md } = useBreakpoint();
  const { i18n, t } = useTranslation();
  const hasHydrated = useHydration();
  const { user } = useAppUserStore();
  const { bgColor, baseSelect, textColorDark } = useTheme();
  const { lang, changeQueryLanguage } = useDetectLanguage(i18n);

  const [code] = useQueryParam('oobCode', StringParam);

  const { mutate: passwordSetting, isPending: passwordSettingLoading } = useSetPassword();
  const { PublicLogo, publicLogoProps, AdditionalLogo, additionalLogoProps } = getCurrentAppTheme();

  const onSubmit = (values: { code: string; password: string }) => {
    if (code) {
      passwordSetting(
        { code, password: values.password },
        {
          onError: () => {
            antdNotification.error({
              title: t('beErrors.wrongLink', {
                defaultValue: t('beErrors.unexpectedError'),
              }),
            });
          },
        },
      );
    }
  };

  useEffect(() => {
    if (hasHydrated && user) {
      navigate(`/${paths.app}`);
    }
  }, [user, hasHydrated]);

  return (
    <>
      <div
        className="hidden sm:block pl-6 sm:pl-0 pt-16 sm:pt-0 rounded-bl-3xl rounded-br-3xl"
        style={{ backgroundColor: sm ? 'transparent' : bgColor }}
      >
        <Title className="text-base-100 mb-0 mt-0 sm:mt-auto">{t('set_password_page.title')}</Title>
        <Text className="text-base-100">
          <Trans
            t={t}
            i18nKey="set_password_page.already_have_an_account"
            components={{
              b: (
                <Button
                  className="inline-flex p-0 text-base-100 underline"
                  type="link"
                  onClick={() => navigate(`/${paths.sign_in}`)}
                />
              ),
            }}
          />
        </Text>
        {isYWC && <PublicLogo {...publicLogoProps} />}
      </div>
      <AntdSpace />
      <Card className="border-none sm:border-solid sm:max-w-md">
        <div className="flex w-full self-start justify-between mb-4">
          {isYWC ? (
            <YWCLogoSmall className="h-20 w-auto md:hidden" />
          ) : (
            <PublicLogo {...publicLogoProps} />
          )}
          <div className="flex items-center gap-2 ml-auto">
            <a
              href={t('faq.link')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 md:hidden"
              style={{ color: textColorDark }}
            >
              {t('faq.text_short')}
              <QuestionCircleOutlined />
            </a>
            <AntdConfigProvider theme={{ components: { Select: baseSelect } }}>
              <AntdSelect
                variant="borderless"
                defaultValue={lang ?? i18n.language}
                onChange={(value) => {
                  changeLanguage(value, i18n);
                  changeQueryLanguage(value as Language);
                }}
                aria-label="Language"
                options={languageOptions()}
              />
            </AntdConfigProvider>
          </div>
        </div>
        <div className="sm:hidden">
          <Title className="mb-0 mt-0 sm:mt-auto" style={{ color: textColorDark }}>
            {t('set_password_page.title')}
          </Title>
        </div>

        <AntdForm
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          requiredMark={false}
          size={md ? 'middle' : 'large'}
        >
          <AntdForm.Item
            label={t('set_password_page.create_password_field_label')}
            name="password"
            rules={[
              {
                message: t('validations.password'),
                pattern: passwordRegEx,
                required: true,
              },
            ]}
          >
            <InputPassword className="h-[48px] sm:h-auto" />
          </AntdForm.Item>

          <AntdForm.Item
            label={t('common.confirm_password')}
            name="confirm_password"
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: t('validations.confirm_password'),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('validations.passwords_not_match')));
                },
              }),
            ]}
          >
            <InputPassword className="h-[48px] sm:h-auto" />
          </AntdForm.Item>

          <AntdForm.Item className="mb-2 mt-8">
            <Button
              className="w-full h-[48px] sm:h-auto"
              htmlType="submit"
              loading={passwordSettingLoading}
              type="primary"
            >
              {passwordSettingLoading ? '' : t('set_password_page.button_submit')}
            </Button>
          </AntdForm.Item>
        </AntdForm>
        <div className="sm:hidden">
          <Trans
            t={t}
            i18nKey="set_password_page.already_have_an_account"
            components={{
              b: (
                <Button
                  className="inline-flex p-0 underline"
                  style={{ color: textColorDark }}
                  type="link"
                  onClick={() => navigate(`/${paths.sign_in}`)}
                />
              ),
            }}
          />
        </div>
        <AdditionalLogo {...additionalLogoProps} />
      </Card>
    </>
  );
};

export default SetupPasswordPage;
